import type { Maybe } from '@adornis/base/utilTypes.js';
import { formatCalendarDay } from '@adornis/datetime/calendarDay.js';

export interface BookingHashInput {
  _class?: string;
  _id?: string;
  userID?: Maybe<string>;
  groupIDs?: Maybe<string[]>;
  startDate?: Maybe<Date>;
  endDate?: Maybe<Date>;
  grade?: Maybe<string>;
  classIdentifier?: Maybe<string>;
  billingPosition?: Maybe<{ priceInCents: Maybe<number> }>;
}

export type BookingHashIngnoreKey = 'userID' | 'grade' | 'classIdentifier' | 'billingPosition' | '_id';

/**
 * Creates a hash for a booking based on the same fields used in bookingIsEqual.
 * @param booking The booking to hash.
 * @param ignoreKeys Array of keys to ignore when hashing the booking.
 * @returns A SHA-256 hash string representing the booking.
 */
export const bookingHash = async (
  booking: BookingHashInput,
  ignoreKeys: BookingHashIngnoreKey[] = ['userID', '_id'],
): Promise<string> => {
  const dataToHash: string[] = [];

  if (!ignoreKeys.includes('_id') && booking._id) {
    dataToHash.push(`_id:${booking._id}`);
  }

  // Add userID to hash if not ignored
  if (!ignoreKeys.includes('userID') && booking.userID) {
    dataToHash.push(`userID:${booking.userID}`);
  }

  // Add groupIDs to hash (sorted to ensure consistent ordering)
  if (booking.groupIDs?.length) {
    const sortedGroupIDs = [...booking.groupIDs].sort();
    dataToHash.push(`groupIDs:${sortedGroupIDs.join(',')}`);
  }

  // Runtimes are canonical calendar days and this hash is computed on both tiers - rendering them in
  // the local zone would give a browser west of Berlin a different hash for the very same booking.
  if (booking.startDate) {
    dataToHash.push(`startDate:${formatCalendarDay(booking.startDate, 'dd.MM.yyyy')}`);
  }

  if (booking.endDate) {
    dataToHash.push(`endDate:${formatCalendarDay(booking.endDate, 'dd.MM.yyyy')}`);
  }

  // Add _class to hash
  dataToHash.push(`_class:${booking._class}`);

  if (!ignoreKeys.includes('grade') && booking.grade) {
    dataToHash.push(`grade:${booking.grade}`);
  }

  if (!ignoreKeys.includes('classIdentifier') && booking.classIdentifier) {
    dataToHash.push(`classIdentifier:${booking.classIdentifier}`);
  }

  // the override is what makes one booking of a group pay differently from the next
  if (!ignoreKeys.includes('billingPosition') && booking.billingPosition) {
    dataToHash.push(`billingPosition:${booking.billingPosition.priceInCents ?? ''}`);
  }

  // Combine all data into a single string
  const combinedData = dataToHash.join('|');

  // Use Web Crypto API (available in both browser and Node.js 16+)
  const encoder = new TextEncoder();
  const data = encoder.encode(combinedData);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);

  // Convert buffer to hex string
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

  return hashHex;
};

/**
 * Checks if two bookings are equal by comparing their hashes.
 * @param booking1 The first booking to compare.
 * @param booking2 The second booking to compare.
 * @param ignoreKeys Array of keys to ignore when comparing the bookings.
 * @returns True if the bookings have the same hash, false otherwise.
 */
export const bookingIsEqualByHash = async (
  booking1: BookingHashInput,
  booking2: BookingHashInput,
  ignoreKeys: BookingHashIngnoreKey[] = ['userID', '_id'],
): Promise<boolean> => {
  const [hash1, hash2] = await Promise.all([bookingHash(booking1, ignoreKeys), bookingHash(booking2, ignoreKeys)]);
  return hash1 === hash2;
};

export const getDataBaseHashForBooking = async (booking: BookingHashInput): Promise<string> => {
  return bookingHash(booking, ['_id', 'billingPosition']);
};
